import { Suspense } from 'react';

import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { CatchBoundary, createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { BusinessLineIcon } from '@components/BusinessLineIcon';
import { DetailItem } from '@components/display/detail-item';
import { Dropdown } from '@components/Dropdown';
import { Anchor } from '@components/ui/anchor';
import { Label } from '@components/ui/label';
import { Separator } from '@components/ui/separator';
import { Tag } from '@components/ui/tag';
import { businessLineStatusLabels } from '@constants/businessLines.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EBusinessLineStatus } from '@enums/businessLine.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { removeUnderscore } from '@formatters/removeUnderscore.formatter';
import { cn } from '@lib/utils';
import { paginationSchema } from '@schemas/pagination.schema';
import { businessLineService } from '@services/businessLines/service';

import { ArchiveBusinessLineAlert } from './@components/ArchiveAlert';
import { BusinessLineDetailsLoading } from './@components/Loading';
import { RestoreBusinessLineAlert } from './@components/RestoreAlert';
import { BusinessLineServices } from './@components/Services';
import { ServicesLoading } from './@components/Services/Loading';

function businessLineQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.businessLines.detail(id),
    queryFn: () => businessLineService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute('/_layout/(private)/business-lines/$id/')({
  validateSearch: search => paginationSchema.parse(search),
  loader: ({ context, params }) => {
    const businessLineOptions = businessLineQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...businessLineOptions,
      revalidateIfStale: true,
    });
  },
  pendingComponent: BusinessLineDetailsLoading,
  pendingMs: 300,
  pendingMinMs: 600,
  component: BusinessLineDetails,
});

function BusinessLineDetails() {
  const { id } = Route.useParams();

  const { data } = useSuspenseQuery(businessLineQueryOptions(id));

  return (
    <>
      <div className="border-secondary mb-6 grid gap-y-2 border-b-2 border-dotted px-2 py-4">
        <div className="flex items-center justify-between">
          <h2
            className="flex flex-1 items-center gap-x-4 text-xl font-bold"
            style={{ color: `#${data.businessLine.color}` }}
          >
            {`${data.businessLine.displayId} - ${removeUnderscore(data.businessLine.name, { capitalize: true })}`}
            <BusinessLineIcon iconName={data.businessLine.icon} />
          </h2>
          {data.businessLine.archived ? (
            <RestoreBusinessLineAlert />
          ) : (
            <ArchiveBusinessLineAlert />
          )}

          <Dropdown
            id={data.businessLine.id}
            options={[
              {
                label: 'Edit business line',
                navigateTo: '/business-lines/$id/update',
                params: { id: data.businessLine.id },
              },
            ]}
          />
        </div>
        <div className="grid gap-y-8 pb-2 lg:grid-cols-2 lg:gap-4 2xl:grid-cols-3">
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              <DetailItem
                label="Creation date"
                type="date"
                value={data.businessLine.createdAt}
              />
              <DetailItem
                className={cn({
                  'text-tertiary':
                    data.businessLine.status === EBusinessLineStatus.Active,
                  'text-warning':
                    data.businessLine.status === EBusinessLineStatus.Inactive,
                })}
                label="Status"
                value={businessLineStatusLabels[data.businessLine.status]}
              />

              <DetailItem
                label="EN name"
                value={data.businessLine.translations.en}
              />
              <DetailItem
                label="FR name"
                value={data.businessLine.translations.fr}
              />
              <DetailItem
                label="NL name"
                value={data.businessLine.translations.nl}
              />
              <DetailItem
                className="capitalize"
                label="Workflow"
                value={removeUnderscore(data.businessLine.workflow?.name)}
              />
              <DetailItem
                isFullLine
                label="Email branding"
                value={data.businessLine.emailBranding}
              />
            </div>

            <Separator
              className="invisible absolute top-0 right-0 h-5/6 lg:visible"
              orientation="vertical"
              variant="dotted"
            />
          </div>

          <div className="grid h-fit grid-cols-2 gap-3 pb-6">
            <DetailItem
              className="capitalize"
              label="Line model"
              value={data.businessLine.model}
            />
            <DetailItem
              label="Max amount int/SR"
              value={
                data.businessLine.maxInterventions === -1
                  ? 'Unlimited'
                  : data.businessLine.maxInterventions
              }
            />

            <DetailItem
              className="capitalize"
              isFullLine
              label="Allocation"
              value={data.businessLine.allocationMode}
            />
          </div>

          <div className="relative col-span-2 flex flex-col gap-y-1 pb-6 2xl:col-span-1 2xl:pl-4">
            <Separator
              className="invisible absolute bottom-0 left-0 h-5/6 2xl:visible"
              orientation="vertical"
              variant="dotted"
            />
            <Label className="text-sm font-normal text-gray-400 dark:text-zinc-400">
              Service types allowed
            </Label>
            <div className="flex h-fit w-fit flex-wrap gap-0.5">
              {data.businessLine.allowedServiceTypes?.map(service => (
                <Tag
                  className={
                    'bg-tertiary-light-three text-tertiary border-tertiary mx-0 rounded-sm border capitalize'
                  }
                  key={service.id}
                >
                  {removeUnderscore(service.name)}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CatchBoundary
        errorComponent={() => (
          <p className="py-4 text-center text-sm text-red-500">
            Failed to load services. Please refresh the page.
          </p>
        )}
        getResetKey={() => 'services'}
      >
        <Suspense fallback={<ServicesLoading />}>
          <BusinessLineServices />
        </Suspense>
      </CatchBoundary>

      <Anchor
        className="size-12"
        float
        params={{ id }}
        rounded="full"
        size="icon"
        to="/business-lines/$id/services/create"
        variant="primary"
      >
        <Plus size={24} />
      </Anchor>
    </>
  );
}
